/** Scenario production rate units (matches scenarios.unit_id in DB). */
export const SCENARIO_UNIT_TONS = 1;
export const SCENARIO_UNIT_BARRELS = 2;

export const OIL_TONS_TO_BARRELS = 7.4;
export const WATER_TONS_TO_BARRELS = 6.1;

/** Convert per-oil-unit price when scenario units change (tons ↔ barrels). */
export function convertOilUnitPrice(price: number, fromUnitId: number, toUnitId: number): number {
  if (fromUnitId === toUnitId || !Number.isFinite(price)) {
    return price;
  }
  if (fromUnitId === SCENARIO_UNIT_BARRELS && toUnitId === SCENARIO_UNIT_TONS) {
    return price * OIL_TONS_TO_BARRELS;
  }
  if (fromUnitId === SCENARIO_UNIT_TONS && toUnitId === SCENARIO_UNIT_BARRELS) {
    return price / OIL_TONS_TO_BARRELS;
  }
  return price;
}

export function convertOilTonsToBarrels(oilTons: number): number {
  return oilTons * OIL_TONS_TO_BARRELS;
}

/** Liquid stored in tons → barrels: oil at 7.4 bbl/t, water at 6.1 bbl/t. */
export function convertLiquidTonsToBarrels(liquidTons: number, oilTons: number): number {
  const waterTons = Math.max(0, liquidTons - oilTons);
  return convertOilTonsToBarrels(oilTons) + waterTons * WATER_TONS_TO_BARRELS;
}

/**
 * Base-well curves from the API are in tons.
 * unit_id=1 → show as tons; unit_id=2 → convert to barrels for display.
 */
export function convertProductionRatesForDisplay(
  unitId: number,
  oilTons: number,
  liquidTons: number,
): { oil: number; liquid: number } {
  if (unitId === SCENARIO_UNIT_BARRELS) {
    return {
      oil: convertOilTonsToBarrels(oilTons),
      liquid: convertLiquidTonsToBarrels(liquidTons, oilTons),
    };
  }
  return { oil: oilTons, liquid: liquidTons };
}

export function getProductionRateAxisLabel(unitId: number): string {
  return unitId === SCENARIO_UNIT_BARRELS
    ? 'Oil / liquid rate (bbl)'
    : 'Oil / liquid rate (t)';
}

export function getCumulativeOilAxisLabel(unitId: number): string {
  return unitId === SCENARIO_UNIT_BARRELS ? 'Cumulative oil (bbl)' : 'Cumulative oil (t)';
}

export function getScenarioUnitLabel(unitId: number): string {
  return unitId === SCENARIO_UNIT_BARRELS ? 'Barrels' : 'Tons';
}

export function getVolumeUnitSuffix(unitId: number): string {
  return unitId === SCENARIO_UNIT_BARRELS ? 'bbl' : 't';
}

export function convertOilVolumeForDisplay(unitId: number, oilTons: number): number {
  if (unitId === SCENARIO_UNIT_BARRELS) {
    return convertOilTonsToBarrels(oilTons);
  }
  return oilTons;
}

export function convertOilVolumeOptional(
  unitId: number,
  value: number | null | undefined,
): number | null {
  if (value == null || typeof value !== 'number' || Number.isNaN(value)) {
    return null;
  }
  return convertOilVolumeForDisplay(unitId, value);
}

export function convertLiquidVolumeOptional(
  unitId: number,
  liquidTons: number | null | undefined,
  oilTons: number | null | undefined,
): number | null {
  if (liquidTons == null || typeof liquidTons !== 'number' || Number.isNaN(liquidTons)) {
    return null;
  }
  if (oilTons == null || typeof oilTons !== 'number' || Number.isNaN(oilTons)) {
    return unitId === SCENARIO_UNIT_BARRELS ? null : liquidTons;
  }
  if (unitId === SCENARIO_UNIT_BARRELS) {
    return convertLiquidTonsToBarrels(liquidTons, oilTons);
  }
  return liquidTons;
}

export function getAnnualProductionAxisLabel(unitId: number): string {
  return unitId === SCENARIO_UNIT_BARRELS ? 'Oil / Liquid Production (1000 bbl)' : 'Oil / Liquid Production (1000 t)';
}

export function getComparisonProductionAxisLabel(unitId: number): string {
  return unitId === SCENARIO_UNIT_BARRELS ? 'Production (1000 bbl)' : 'Production (1000 t)';
}

export function getComparisonDifferenceAxisLabel(unitId: number): string {
  return unitId === SCENARIO_UNIT_BARRELS ? 'Difference (1000 bbl)' : 'Difference (1000 t)';
}

export function formatThousandVolumeTooltip(value: number, unitId: number): string {
  const unitLabel = unitId === SCENARIO_UNIT_BARRELS ? 'thousand barrels' : 'thousand tons';
  return `${Number(value).toFixed(2)} ${unitLabel}`;
}

/** Convert paired oil/liquid volumes from API (tons) for comparison charts. */
export function convertComparisonOilLiquid(
  unitId: number,
  oilTons: number | null | undefined,
  liquidTons: number | null | undefined,
): { oil: number | null; liquid: number | null } {
  if (oilTons == null && liquidTons == null) {
    return { oil: null, liquid: null };
  }
  const oilRaw = oilTons ?? null;
  const liquidRaw = liquidTons ?? null;
  return {
    oil: convertOilVolumeOptional(unitId, oilRaw),
    liquid: convertLiquidVolumeOptional(unitId, liquidRaw, oilRaw),
  };
}

export function getBaseWellOilLiquidAxisLabel(unitId: number): string {
  return unitId === SCENARIO_UNIT_BARRELS ? 'Oil / Liquid (bbl)' : 'Oil / Liquid (t)';
}

export function getQ0ValueAxisLabel(unitId: number): string {
  return unitId === SCENARIO_UNIT_BARRELS ? 'Q0 (bbl)' : 'Q0 (t)';
}

/** Unit suffix for oil production columns in scenario ZIP export (matches chart axis). */
export function getExportOilVolumeUnitLabel(unitId: number): string {
  return unitId === SCENARIO_UNIT_BARRELS ? '1000 bbl' : '1000 t';
}

export function getExportOilProductionColumnLabel(percentile: string, unitId: number): string {
  return `Oil production ${percentile} (${getExportOilVolumeUnitLabel(unitId)})`;
}

export function getExportLiquidProductionColumnLabel(percentile: string, unitId: number): string {
  return `Liquid production ${percentile} (${getExportOilVolumeUnitLabel(unitId)})`;
}

/** Oil price per metric ton (DB volumes are in thousands of tons). */
export function getEconomicsOilPriceLabel(unitId: number): string {
  return unitId === SCENARIO_UNIT_BARRELS ? 'Oil price (bbl)' : 'Oil price (t)';
}

export const ECONOMICS_INJECTION_COST_LABEL = 'Injection cost (m³)';

export function getEconomicsTransportLabel(unitId: number): string {
  return unitId === SCENARIO_UNIT_BARRELS ? 'Transport (bbl)' : 'Transport (t)';
}

export function getEconomicsAdditionalOilLabel(unitId: number): string {
  return unitId === SCENARIO_UNIT_BARRELS ? 'Additional / oil (bbl)' : 'Additional / oil (t)';
}

export function getExportWaterCutColumnLabel(percentile: string): string {
  return `Water cut ${percentile} (%)`;
}

export type ProductionExportField =
  | 'production'
  | 'productionP10'
  | 'productionP50'
  | 'productionP90'
  | 'liquid'
  | 'liquidP10'
  | 'liquidP50'
  | 'liquidP90'
  | 'waterCut'
  | 'waterCutP10'
  | 'waterCutP50'
  | 'waterCutP90';

/** Column order for reservoir/total production CSV export (matches chart series). */
export function getProductionExportColumns(unitId: number): Array<{ field: ProductionExportField; header: string }> {
  return [
    { field: 'production', header: getExportOilProductionColumnLabel('ML', unitId) },
    { field: 'productionP10', header: getExportOilProductionColumnLabel('P10', unitId) },
    { field: 'productionP50', header: getExportOilProductionColumnLabel('P50', unitId) },
    { field: 'productionP90', header: getExportOilProductionColumnLabel('P90', unitId) },
    { field: 'liquid', header: getExportLiquidProductionColumnLabel('ML', unitId) },
    { field: 'liquidP10', header: getExportLiquidProductionColumnLabel('P10', unitId) },
    { field: 'liquidP50', header: getExportLiquidProductionColumnLabel('P50', unitId) },
    { field: 'liquidP90', header: getExportLiquidProductionColumnLabel('P90', unitId) },
    { field: 'waterCut', header: getExportWaterCutColumnLabel('ML') },
    { field: 'waterCutP10', header: getExportWaterCutColumnLabel('P10') },
    { field: 'waterCutP50', header: getExportWaterCutColumnLabel('P50') },
    { field: 'waterCutP90', header: getExportWaterCutColumnLabel('P90') },
  ];
}

export function formatProductionExportRow(row: ProductionChartRow, columns: Array<{ field: ProductionExportField }>): string {
  return columns
    .map(({ field }) => formatExportChartNumber(row[field] as number | null | undefined))
    .join(';');
}

export function formatExportChartNumber(value: number | null | undefined): string {
  if (value == null || typeof value !== 'number' || Number.isNaN(value)) {
    return '';
  }
  return String(value);
}

export type ProductionChartRow = {
  year: number;
  production: number | null;
  productionP10?: number | null;
  productionP50?: number | null;
  productionP90?: number | null;
  liquid?: number | null;
  liquidP10?: number | null;
  liquidP50?: number | null;
  liquidP90?: number | null;
  waterCut?: number | null;
  waterCutP10?: number | null;
  waterCutP50?: number | null;
  waterCutP90?: number | null;
  [key: string]: unknown;
};

/** Convert oil/liquid series for display; water cut % is unchanged. */
export function convertProductionChartRow<T extends ProductionChartRow>(unitId: number, row: T): T {
  if (unitId === SCENARIO_UNIT_TONS) {
    return row;
  }
  const rawProd = row.production;
  const rawP10 = row.productionP10 ?? null;
  const rawP50 = row.productionP50 ?? null;
  const rawP90 = row.productionP90 ?? null;
  return {
    ...row,
    production: convertOilVolumeOptional(unitId, rawProd),
    productionP10: convertOilVolumeOptional(unitId, rawP10),
    productionP50: convertOilVolumeOptional(unitId, rawP50),
    productionP90: convertOilVolumeOptional(unitId, rawP90),
    liquid: convertLiquidVolumeOptional(unitId, row.liquid ?? null, rawProd),
    liquidP10: convertLiquidVolumeOptional(unitId, row.liquidP10 ?? null, rawP10),
    liquidP50: convertLiquidVolumeOptional(unitId, row.liquidP50 ?? null, rawP50),
    liquidP90: convertLiquidVolumeOptional(unitId, row.liquidP90 ?? null, rawP90),
  };
}

export function convertProductionChartData<T extends ProductionChartRow>(unitId: number, rows: T[]): T[] {
  return rows.map((row) => convertProductionChartRow(unitId, row));
}

export type BaseWellChartPoint = Record<string, unknown> & {
  year?: number;
  scenario?: number | null;
  P10?: number | null;
  P50?: number | null;
  P90?: number | null;
  min?: number | null;
  max?: number | null;
  baseWellLiquid?: number | null;
  baseWellLiquidP10?: number | null;
  baseWellLiquidP50?: number | null;
  baseWellLiquidP90?: number | null;
};

/** Convert base-well oil/liquid chart points for display; WC % unchanged. */
export function convertBaseWellChartPoint(unitId: number, point: BaseWellChartPoint): BaseWellChartPoint {
  if (unitId === SCENARIO_UNIT_TONS) {
    return point;
  }
  const rawOilMl = point.scenario;
  const rawOilP10 = point.P10;
  const rawOilP50 = point.P50;
  const rawOilP90 = point.P90;
  const next: BaseWellChartPoint = { ...point };

  const convOilKey = (key: string, raw: number | null | undefined) => {
    if (raw != null && typeof raw === 'number' && !Number.isNaN(raw)) {
      next[key] = convertOilVolumeForDisplay(unitId, raw);
    }
  };

  convOilKey('scenario', rawOilMl as number | null | undefined);
  convOilKey('P10', rawOilP10 as number | null | undefined);
  convOilKey('P50', rawOilP50 as number | null | undefined);
  convOilKey('P90', rawOilP90 as number | null | undefined);
  convOilKey('min', point.min as number | null | undefined);
  convOilKey('max', point.max as number | null | undefined);

  Object.keys(point).forEach((key) => {
    if (key.startsWith('analogue_') && typeof point[key] === 'number') {
      next[key] = convertOilVolumeForDisplay(unitId, point[key] as number);
    }
  });

  const convLiqKey = (liqKey: string, oilRaw: number | null | undefined) => {
    const liqRaw = point[liqKey];
    if (liqRaw != null && typeof liqRaw === 'number' && !Number.isNaN(liqRaw)) {
      next[liqKey] = convertLiquidVolumeOptional(unitId, liqRaw, oilRaw ?? null);
    }
  };

  convLiqKey('baseWellLiquid', rawOilMl as number | null | undefined);
  convLiqKey('baseWellLiquidP10', rawOilP10 as number | null | undefined);
  convLiqKey('baseWellLiquidP50', rawOilP50 as number | null | undefined);
  convLiqKey('baseWellLiquidP90', rawOilP90 as number | null | undefined);

  return next;
}

export function convertBaseWellChartData(unitId: number, rows: BaseWellChartPoint[]): BaseWellChartPoint[] {
  return rows.map((row) => convertBaseWellChartPoint(unitId, row));
}
